import { map } from 'lodash';
import React, { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerCancelReason, EventName } from 'enums';
import { TranslationNamespace } from 'i18n';
import { eventUtils } from 'utils';

import { Modal } from '../Modal';

export type CancelSurveyFeedback = {
  reasons: CustomerCancelReason[];
  comment: string;
};

type Props = {
  open: boolean;
  reasons: CustomerCancelReason[];
  onConfirm: (feedback: CancelSurveyFeedback) => void;
  onDismiss: () => void;
  isLoading?: boolean;
};

const COMMENT_MAX_LENGTH = 300;

export const CancelSurveyModal: React.FC<Props> = ({
  open,
  reasons,
  onConfirm,
  onDismiss,
  isLoading,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.cancel_survey_modal',
  });

  const [selectedReasons, setSelectedReasons] = useState<
    CustomerCancelReason[]
  >([]);
  const [comment, setComment] = useState('');

  const toggleReason = useCallback((reason: CustomerCancelReason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason],
    );
  }, []);

  const handleConfirm = useCallback(() => {
    eventUtils.trackEvent({
      event: EventName.CancelSurveyConfirm,
      message: JSON.stringify({ reasons: selectedReasons, comment }),
    });
    onConfirm({ reasons: selectedReasons, comment });
    setSelectedReasons([]);
    setComment('');
  }, [onConfirm, selectedReasons, comment]);

  const handleDismiss = useCallback(() => {
    eventUtils.trackEvent({
      event: EventName.CancelSurveyDismiss,
    });
    onDismiss();
    setSelectedReasons([]);
    setComment('');
  }, [onDismiss]);

  return (
    <Modal
      open={open}
      onClose={handleDismiss}
      title={t('title')}
      footer={
        <Fragment>
          <button
            className="tw-w-full tw-h-[54px] tw-rounded-[24px] tw-bg-primary-button tw-text-primary-button tw-font-medium tw-text-base tw-border-none tw-cursor-pointer tw-mb-2"
            onClick={handleConfirm}
          >
            {t('confirm_button')}
          </button>
          <button
            className="tw-w-full tw-h-[54px] tw-rounded-[24px] tw-bg-transparent tw-text-cancel-action tw-font-medium tw-text-base tw-border-none tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            onClick={handleDismiss}
            disabled={isLoading}
          >
            {t('dismiss_button')}
          </button>
        </Fragment>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        {map(reasons, (value) => (
          <label
            key={value}
            className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer tw-text-sm tw-text-primary"
          >
            <input
              type="checkbox"
              checked={selectedReasons.includes(value)}
              onChange={() => toggleReason(value)}
              className="tw-w-4 tw-h-4 tw-cursor-pointer tw-accent-primary-button"
            />
            {t(`reasons.${value}`)}
          </label>
        ))}
        <div className="tw-mt-1">
          <textarea
            value={comment}
            onChange={(e) =>
              setComment(e.target.value.slice(0, COMMENT_MAX_LENGTH))
            }
            placeholder={t('comment_placeholder')}
            maxLength={COMMENT_MAX_LENGTH}
            rows={6}
            className="tw-w-full tw-resize-none tw-rounded-xl tw-border tw-border-layout-accent tw-bg-layout tw-text-primary tw-text-sm tw-p-3 tw-outline-none focus:tw-border-primary-button"
          />
          <div className="tw-text-right tw-text-xs tw-text-secondary tw-mt-1">
            {`${comment.length}/${COMMENT_MAX_LENGTH}`}
          </div>
        </div>
      </div>
    </Modal>
  );
};
